<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import * as Sentry from '@sentry/vue'
import type { TLiveVideoPlaybackStatus } from '@/store/liveTrivia/state'

const { stream, playbackStatus } = defineProps<{
    stream: MediaStream | null
    playbackStatus: TLiveVideoPlaybackStatus
}>()

/** Starts true: browsers only autoplay muted video. Any tap on the page can turn the sound on. */
const isMuted = defineModel<boolean>('isMuted', { required: true })

const videoElement = ref<HTMLVideoElement | null>(null)
/** The browser refused even muted playback (e.g. iOS Low Power Mode), so a tap has to start it */
const needsTapToPlay = ref(false)

const isPlaying = computed(() => playbackStatus === 'playing' && !!stream)
/** Once the host has been on screen, waiting means they stopped, not that they haven't started */
const hasPlayed = ref(false)
watch(isPlaying, isNowPlaying => {
    if (isNowPlaying) {
        hasPlayed.value = true
    }
}, { immediate: true })

const statusMessage = computed(() => {
    switch (playbackStatus) {
        case 'connecting':
            return 'Connecting to the host\'s video…'
        case 'waitingForHost':
            return hasPlayed.value ? 'The host\'s video paused. It\'ll be back shortly.' : 'Host video starts soon'
        case 'ended':
            return 'The host\'s video has ended'
        default:
            return ''
    }
})

const isPlaybackRefusal = (error: unknown) => error instanceof DOMException && error.name === 'NotAllowedError'
/** A newer srcObject or pause interrupted play(); the next play() takes over */
const isPlaybackInterruption = (error: unknown) => error instanceof DOMException && error.name === 'AbortError'

const playVideo = async () => {
    const videoEl = videoElement.value
    if (!videoEl?.srcObject) {
        return
    }
    try {
        await videoEl.play()
        needsTapToPlay.value = false
    } catch (error) {
        if (isPlaybackInterruption(error)) {
            return
        }
        if (!isPlaybackRefusal(error)) {
            Sentry.captureException(error)
            return
        }
        // Sound needs a user gesture; fall back to muted rather than showing nothing
        if (!videoEl.muted) {
            isMuted.value = true
            videoEl.muted = true
            await playVideo()
            return
        }
        needsTapToPlay.value = true
    }
}

const handleUnmute = async () => {
    isMuted.value = false
    const videoEl = videoElement.value
    if (videoEl) {
        // Set it directly too, so play() below runs unmuted inside this tap's user gesture
        videoEl.muted = false
    }
    await playVideo()
}

// Also fires when the element mounts, in case the stream was already there
watch([ videoElement, () => stream ], async ([ videoEl, newStream ]) => {
    if (!videoEl || videoEl.srcObject === newStream) {
        return
    }
    videoEl.srcObject = newStream
    await playVideo()
})

// Unmuting from outside (the Join tap) is a user gesture too; resume playback in case it was refused
watch(isMuted, async isNowMuted => {
    await nextTick()
    if (!isNowMuted && needsTapToPlay.value) {
        await playVideo()
    }
})
</script>

<template>
    <div class="live-video-player">
        <!-- Captions aren't available for a live WebRTC stream -->
        <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
        <video
            ref="videoElement"
            class="live-video-player__video"
            :class="{ 'live-video-player__video--hidden': !isPlaying }"
            aria-label="Host video"
            autoplay
            playsinline
            :muted="isMuted"
        />
        <p
            v-if="!isPlaying && statusMessage"
            class="live-video-player__status"
            role="status"
        >
            {{ statusMessage }}
        </p>
        <button
            v-if="isPlaying && (isMuted || needsTapToPlay)"
            type="button"
            class="live-video-player__sound-btn"
            @click="handleUnmute"
        >
            {{ needsTapToPlay ? 'Tap to play' : 'Tap for sound' }}
        </button>
        <button
            v-else-if="isPlaying"
            type="button"
            class="live-video-player__sound-btn live-video-player__sound-btn--subtle"
            @click="isMuted = true"
        >
            Mute
        </button>
    </div>
</template>

<style lang="scss" scoped>
.live-video-player {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 16 / 9;
    margin-bottom: 16px;
    border-radius: 8px;
    overflow: hidden;
    background: $brand-black;

    @include breakpoint(black-bear) {
        max-height: 168px;
        margin-bottom: 12px;
    }

    &__video {
        width: 100%;
        height: 100%;
        object-fit: contain;

        &--hidden {
            visibility: hidden;
        }
    }

    &__status {
        position: absolute;
        margin: 0;
        padding: 0 16px;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        text-align: center;
        color: $white;
    }

    &__sound-btn {
        position: absolute;
        right: 8px;
        bottom: 8px;
        padding: 6px 12px;
        border: none;
        border-radius: 16px;
        background: $banana-bread;
        color: $brand-black;
        font-size: 13px;
        font-weight: 700;
        line-height: 18px;
        cursor: pointer;

        &--subtle {
            background: rgba($brand-black, 0.6);
            color: $white;
        }
    }
}
</style>
